/**
 * Canvas2D — which the TSS 3D-UI labels (WalletCounterLabel, DropZoneOwnerLabel,
 * opponent dice labels) and BettingHUD3D all draw with — renders text using only
 * fonts ALREADY in the browser's font cache. Unlike DOM text, a canvas draw does
 * NOT kick off @font-face loading, so a label drawn before its font is fetched
 * renders in the fallback and then visibly swaps once the font arrives (e.g. the
 * wallet bet-total popping in as Oswald a beat after the number prints).
 *
 * We eagerly fetch every canvas-drawn font at boot via the FontFace API so the
 * very first draw is already correct. Self-hosted Oswald (font-display:block) is
 * the one that bit us; the Google families are included as cheap insurance.
 */

// CSS font shorthands — family + weight select the face; the size is irrelevant
// to the fetch but the shorthand must be syntactically valid to parse.
const CANVAS_FONTS = [
  '400 34px Oswald',
  '700 34px Oswald',
  '400 24px "Bebas Neue"',
  '400 20px Rajdhani',
  '700 20px Rajdhani',
] as const;

let preloadPromise: Promise<void> | null = null;

/**
 * Fetch all canvas-drawn fonts so they're cache-resident before the first label
 * renders. Idempotent and safe to call as early as possible in boot. Resolves
 * once every face has loaded (or failed — failures fall back to sans-serif, the
 * prior behaviour, so this never blocks the app).
 */
export function preloadCanvasFonts(): Promise<void> {
  if (preloadPromise) return preloadPromise;
  if (!document.fonts?.load) {
    preloadPromise = Promise.resolve();
    return preloadPromise;
  }
  preloadPromise = Promise.all(
    CANVAS_FONTS.map((font) => document.fonts.load(font).catch(() => undefined)),
  ).then(() => undefined);
  return preloadPromise;
}
